<template>
  <div
    ref="wrapperRef"
    class="SelectDropdown relative w-full cursor-pointer select-none bg-bg-base"
    :class="{
      'h-32': design === 'sm',
      'h-36': design === 'md',
      'h-40': design === 'base',
      'h-48': design === 'lg',
      'mb-12': showError && errors.length,
    }"
    tabindex="0"
    aria-haspopup="listbox"
    :aria-expanded="expanded"
    :aria-label="label ?? undefined"
    @click="expanded = !expanded"
    @keydown.enter.prevent="expanded = !expanded"
    @keydown.space.prevent="expanded = !expanded"
    @keydown.escape="expanded = false"
  >
    <!-- * * * *  label -->
    <div
      v-if="label"
      aria-hidden="true"
      class="absolute left-12
      type-sm leading-single text-darker
      pointer-events-none select-none
      transition-all"
      :class="{
        'top-[9px]': design === 'sm' && !isFilled,
        'top-[12px]': design === 'md' && !isFilled,
        'top-[14px]': design === 'base' && !isFilled,
        'top-[16px]': design === 'lg' && !isFilled,
        'top-[3px] text-8 desk:text-10': design === 'sm' && isFilled,
        'top-[4px] text-8 desk:text-10': design === 'md' && isFilled,
        'top-[6px] type-xxs': design === 'base' && isFilled,
        'top-[8px] type-xxs': design === 'lg' && isFilled,

      }"
    >
      {{ label }}
    </div>

    <!-- * * * *  expand button -->
    <div
      class="absolute right-12"
      :class="{
        'top-[8px]': design === 'sm',
        'top-[9px]': design === 'md',
        'top-[11px]': design === 'base',
        'top-[14px]': design === 'lg',
      }"
    >
      <img
        src="/icons/angle-up.svg"
        class="w-20 h-20 transition-all pointer-events-none"
        :class="{
          'rotate-180': !expanded
        }"
        alt=""
      >
    </div>

    <!-- * * * *  selected option -->
    <div
      class="w-full pl-12 type-sm leading-single text-darkest border"
      :class="{
        'h-32': design === 'sm',
        'h-36': design === 'md',
        'h-40': design === 'base',
        'h-48': design === 'lg',
        'pt-[6px]': design === 'sm' && !label,
        'pt-[8px]': design === 'md' && !label,
        'pt-[10px]': design === 'base' && !label,
        'pt-[16px]': design === 'lg' && !label,
        'pt-[11px]': design === 'sm' && label,
        'pt-[15px]': design === 'md' && label,
        'pt-[20px]': design === 'base' && label,
        'pt-[24px]': design === 'lg' && label,

        'border-border-strong': expanded,
        'border-border-base': !expanded,
        'invalid veeValidateFail border-border-errorStrong outline outline-criticalDark' : showError
      }"
    >
      {{ shown }}
    </div>

    <!-- * * * * expanded options -->
    <div
      v-if="expanded"
      role="listbox"
      class="absolute left-0 w-full z-modals bg-lightest outline outline-1 outline-bg-inverse/10 p-16 shadow-lg overflow-auto"
      :class="{
        'top-[38px]': !openAbove && design === 'sm',
        'top-[42px]': !openAbove && design === 'md',
        'top-[46px]': !openAbove && design === 'base',
        'top-[52px]': !openAbove && design === 'lg',
        'bottom-[38px]': openAbove && design === 'sm',
        'bottom-[42px]': openAbove && design === 'md',
        'bottom-[46px]': openAbove && design === 'base',
        'bottom-[52px]': openAbove && design === 'lg',
      }"
      :style="{ maxHeight: `${maxDropdownHeight}px` }"
    >
      <div
        v-for="option in options"
        :key="option.key"
        role="option"
        :aria-selected="option.key === value"
        class="flex items-start mb-16 last-of-type:mb-0 font-normal"
        @click.stop="clickOption(option.key)"
      >
        <div class="basis-20">
          <div
            class="radioInput"
            :class="{
              'checked':option.key === value
            }"
          />
        </div>
        <div class="pl-16">
          <div class="type-sm">{{ option.title }}</div>
          <div
            v-if="option.text"
            class="type-xs text-dark"
          >
            {{ option.text }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="showError && errors.length" class="pointer-events-none select-none mt-8 h-16 relative">
      <div
        v-for="(error, ei) in errors"
        :key="ei"
        class="flex flex-row items-center gap-4 whitespace-nowrap"
      >
        <img
          src="/icons/exclamation-circle-critical.svg"
          class="w-16 h-16"
          alt=""
        >
        <div class="type-xs-medium leading-single text-criticalDark">{{ error }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';

const value = defineModel<string | number | null>();

const wrapperRef = useTemplateRef('wrapperRef');
const openAbove = ref(false);
const maxDropdownHeight = ref(400);

const checkPosition = () => {
  const el = wrapperRef.value as HTMLElement | null;
  if (!el) {return;}
  const rect = el.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom - 16;
  const spaceAbove = rect.top - 16;
  if (spaceBelow < 400 && spaceAbove > spaceBelow) {
    openAbove.value = true;
    maxDropdownHeight.value = Math.min(400, spaceAbove);
  } else {
    openAbove.value = false;
    maxDropdownHeight.value = Math.min(400, spaceBelow);
  }
};

export type SelectOptions = {
  key: string | number,
  title: string,
  text?: string
}

interface Props {
  label?: string | null,
  options: SelectOptions[],
  showError?: boolean;
  errors?: string[];
  design?: 'lg' | 'base' | 'md' | 'sm';
  startOpen?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  label: null,
  showError: false,
  errors: () => [],
  design: 'lg',
  startOpen: false,
});

const expanded = ref(false);

onMounted(()=> {
  if (props.startOpen) {
    expanded.value = true;
  }
});

const shown = computed(()=> {
  return props.options.find((f)=> f.key === value.value)?.title || '';
});

const clickOption = (key: string) => {
  value.value = key;
  expanded.value = false;
};

const isFilled = computed(()=> {
  if (!value.value) {
    return false;
  }
  if (typeof value.value === 'string' && value.value === '') {
    return  false;
  }
  return true;

});

watch(expanded, (val) => {
  if (val) {checkPosition();}
  else {openAbove.value = false;}
});

onClickOutside(wrapperRef, () => {
  expanded.value = false;
});

</script>
